import { useRef } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactCard from '../components/ContactCard';

const projects = [
  {
    title: "M2 Air Solution",
    image: require('../assets/m2air.png'),
    url: "https://m2airsolution.com",
  },
  {
    title: "PSA Toronto",
    image: require('../assets/psa.png'),
    url: "https://psatoronto.net",
  },
];

const projectHighlights = [
  {
    metric: '35+',
    label: 'sites & funnels launched',
    copy: 'Each one engineered to hit aggressive growth targets while reflecting the brand story.',
  },
  {
    metric: '4.7x',
    label: 'average ROI within 6 months',
    copy: 'We stay locked in after launch with optimisation sprints and data-driven experiments.',
  },
  {
    metric: '14 days',
    label: 'from kickoff to first release',
    copy: 'Agile pods ship fast so you can test, learn, and scale without waiting months.',
  },
];

const buildProcess = [
  {
    title: 'Discover & define',
    text: 'We align on goals, audience, and success metrics. Expect a deep-dive workshop and rapid prototypes.',
  },
  {
    title: 'Design & build',
    text: 'Our designers, developers, and copywriters collaborate in sprints to deliver conversion-ready experiences.',
  },
  {
    title: 'Launch & optimise',
    text: 'We deploy with robust tracking, then iterate weekly on CRO, creative, and messaging to maximise ROI.',
  },
];

export default function ProjectsPage() {
  const contactRef = useRef(null);

  const handleTalkClick = () => {
    contactRef.current?.focusForm?.();
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-white via-sky-50 to-indigo-100 font-sans text-slate-900">
      <Navbar onTalkClick={handleTalkClick} />

      <main className="flex-1 pt-32">
        <section id="services" className="px-6">
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-sm uppercase tracking-[0.2em] text-slate-500">Selected work</p>
            <h1 className="mt-4 text-4xl font-semibold md:text-5xl">Sites and campaigns built to convert</h1>
            <p className="mt-4 text-lg text-slate-600">
              We partner with ambitious teams to ship high-performing digital experiences. Here’s a sample of recent
              launches across web, apps, and paid media.
            </p>
          </div>

          <div className="mx-auto mt-16 grid max-w-6xl gap-10 md:grid-cols-2">
            {projects.map((project, index) => (
              <motion.a
                key={project.title}
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block overflow-hidden rounded-3xl border border-slate-200 bg-white/70 shadow-sm backdrop-blur transition hover:-translate-y-1 hover:shadow-lg"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.15 }}
                viewport={{ once: true, amount: 0.2 }}
              >
                <img
                  src={project.image}
                  alt={project.title}
                  className="h-64 w-full object-cover transition duration-500 group-hover:scale-105"
                />
                <div className="border-t border-slate-200 bg-gradient-to-r from-white to-slate-50 p-6">
                  <h3 className="text-2xl font-semibold text-slate-900">{project.title}</h3>
                  {project.description ? (
                    <p className="mt-2 text-sm text-slate-600">{project.description}</p>
                  ) : null}
                  <p className="mt-2 text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Visit site →</p>
                </div>
              </motion.a>
            ))}
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-6xl px-6">
          <div className="grid gap-6 md:grid-cols-3">
            {projectHighlights.map((highlight) => (
              <div
                key={highlight.label}
                className="rounded-3xl border border-slate-200 bg-white/80 p-6 text-center shadow-sm backdrop-blur"
              >
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{highlight.label}</p>
                <p className="mt-4 text-3xl font-semibold text-slate-900">{highlight.metric}</p>
                <p className="mt-3 text-sm text-slate-600">{highlight.copy}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="work" className="mx-auto mt-16 max-w-6xl px-6">
          <div className="rounded-3xl border border-slate-200 bg-white/80 p-10 shadow-sm backdrop-blur">
            <p className="text-sm uppercase tracking-[0.2em] text-slate-500">Proof</p>
            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <h2 className="text-3xl font-semibold text-slate-900">What partnering with Anorc looks like</h2>
                <p className="mt-3 text-sm text-slate-600">
                  We act as an extension of your team — owning strategy, design, development, creative, and performance marketing so
                  every launch is built for growth from day one.
                </p>
              </div>
              <div className="rounded-2xl bg-slate-900/90 p-6 text-white">
                <p className="text-sm uppercase tracking-[0.2em] text-slate-300">Wins we deliver</p>
                <ul className="mt-4 space-y-3 text-sm text-slate-100">
                  <li>• 312% increase in organic demos within 6 months</li>
                  <li>• 63 paid trials in 14 days for a community launch</li>
                  <li>• 48% lower CPL across paid media funnels</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="process" className="mx-auto mt-16 max-w-6xl px-6">
          <div className="rounded-3xl border border-slate-200 bg-white/80 p-10 shadow-sm backdrop-blur">
            <h2 className="text-3xl font-semibold text-slate-900">Our launch + growth methodology</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {buildProcess.map((step) => (
                <div key={step.title} className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-500">{step.title}</h3>
                  <p className="mt-3 text-sm text-slate-600">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-4xl px-6 text-center">
          <div className="rounded-3xl border border-blue-100 bg-white/80 p-10 shadow-sm backdrop-blur">
            <h2 className="text-3xl font-semibold text-slate-900 md:text-4xl">Need results like these?</h2>
            <p className="mt-3 text-lg text-slate-600">
              Share your goals and we’ll show you the roadmap we use to engineer wins for every engagement.
            </p>
            <button
              type="button"
              onClick={handleTalkClick}
              className="mt-6 inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-white shadow hover:opacity-90"
            >
              Talk to the team →
            </button>
          </div>
        </section>
      </main>

      <ContactCard ref={contactRef} className="mt-16" />
      <Footer />
    </div>
  );
}
